/**
 * The support role matrix, stated once on the client — Task 13.1, requirements 9.3 and 10.1.
 *
 * The server is the authority: Task 6 enforces every one of these and a forged claim changes nothing. This
 * exists for the other half of the requirement — "perform no request behind a forbidden control" — which is
 * a client question. A dashboard that renders a reassign control to an `agente` and lets them press it is
 * asking the API for a 403 it already knew it would get, and showing an error for something that was never
 * offered honestly.
 *
 * Kept as data rather than as conditions scattered through components, so 13.2 and 13.3 read the same table
 * the tests do, and so a role gaining a capability is one edit.
 */
import {
  supportRequestStatuses,
  type StaffRole,
  type SupportRequestStatus,
} from '@apuestapr/contracts';

/**
 * Every support operation the dashboard can perform.
 *
 * `claimSelf` and `assignAny` are separate because the API separates them: all three roles may take a request
 * themselves, and only manager and admin may hand one to somebody else. Collapsing them into "assign" is what
 * would produce a reassign control for an agente.
 */
export type SupportCapability =
  | 'read'
  | 'note'
  | 'claimSelf'
  | 'progress'
  | 'reopen'
  | 'close'
  | 'markSpam'
  | 'assignAny'
  | 'recordRetentionHold';

const editorial: readonly SupportCapability[] = [
  'read',
  'note',
  'claimSelf',
  'progress',
  'reopen',
  'close',
  'markSpam',
  'assignAny',
  'recordRetentionHold',
];

/**
 * `agente` reads, notes, claims a request for itself, and moves its own work forward. It does not reopen,
 * close, mark spam, reassign, or record a retention hold.
 */
const agente: readonly SupportCapability[] = ['read', 'note', 'claimSelf', 'progress'];

export const supportCapabilities: Readonly<Record<StaffRole, readonly SupportCapability[]>> = {
  admin: editorial,
  manager: editorial,
  agente,
};

export const canSupport = (role: StaffRole, capability: SupportCapability): boolean =>
  supportCapabilities[role].includes(capability);

/**
 * The status transitions the server permits, mirrored — Task 13.3.
 *
 * This is a deliberate second copy of `canChangeStatus` in `apps/api/src/support/admin-service.ts`, and the
 * server remains the only authority: it re-checks every one of these and answers 403 or 409 regardless of what
 * the browser believed. The copy exists because the requirement is "perform no request behind a forbidden
 * control", and a client cannot decide which buttons to render without knowing the table.
 *
 * The shape follows the server's exactly, including the two rules that hold for every role — a status may
 * always be set to itself, `new` may advance to `in_progress`, and `in_progress` may advance to `resolved` —
 * and the fact that `spam` is terminal for everyone. `permissions.test.ts` states the whole table as data so a
 * server change that is not mirrored here shows up as a failing expectation rather than as a 403 a user meets.
 */
const advancedTransitions: Readonly<Record<SupportRequestStatus, readonly SupportRequestStatus[]>> =
  {
    new: ['closed', 'spam'],
    in_progress: ['closed', 'spam'],
    resolved: ['in_progress', 'closed', 'spam'],
    closed: ['in_progress', 'spam'],
    // Terminal for every role. Marking something as spam is the end of the conversation.
    spam: [],
  };

/** Available to every support role, because progressing your own work is the job. */
const universalTransitions: Readonly<
  Record<SupportRequestStatus, readonly SupportRequestStatus[]>
> = {
  new: ['in_progress'],
  in_progress: ['resolved'],
  resolved: [],
  closed: [],
  spam: [],
};

export const canChangeSupportStatus = (
  from: SupportRequestStatus,
  to: SupportRequestStatus,
  role: StaffRole,
): boolean => {
  if (from === to) return true;
  if (universalTransitions[from].includes(to)) return true;
  return canSupport(role, 'reopen') && advancedTransitions[from].includes(to);
};

/**
 * The statuses a role may move a request to from where it is now, excluding the current one.
 *
 * Ordered by the vocabulary rather than by permission, so the controls do not reshuffle when a manager and an
 * agente look at the same request.
 */
export const allowedSupportStatusChanges = (
  from: SupportRequestStatus,
  role: StaffRole,
): readonly SupportRequestStatus[] =>
  supportRequestStatuses.filter((to) => to !== from && canChangeSupportStatus(from, to, role));

/**
 * Every authenticated staff role reaches the support screens. That is deliberate and is the difference from
 * the Content Console, which `agente` cannot open at all: triage is the work an agente is for.
 */
export const canOpenSupport = (role: StaffRole): boolean => canSupport(role, 'read');
